import React, { useState, useEffect, useCallback } from 'react';
import { ClockIcon, XIcon, CheckCircleIcon, TrendingUpIcon } from './icons/InterfaceIcons';
import { parseTextWithImages } from './parseTextWithImages';

interface AdaptiveQuestion {
  id: string;
  text: string;
  options: string[];
  answer: string;
  difficulty: number; // 1-10 scale
  subject: string;
  topic: string;
}

interface AdaptiveTestState {
  currentQuestion: AdaptiveQuestion | null;
  questionHistory: { question: AdaptiveQuestion; userAnswer: string; isCorrect: boolean; timeTaken: number }[];
  estimatedAbility: number; // Theta in IRT model
  standardError: number;
  questionsAnswered: number;
  timeLeft: number;
  testStatus: 'instructions' | 'running' | 'completed';
  currentDifficulty: number;
}

const INITIAL_ABILITY = 0; // Start at average ability
const MAX_QUESTIONS = 20;
const TIME_LIMIT = 30 * 60; // 30 minutes

export const AdaptiveTest: React.FC<{ 
  onClose: () => void;
  subject: string;
  onComplete: (results: any) => void;
}> = ({ onClose, subject, onComplete }) => {
  const [testState, setTestState] = useState<AdaptiveTestState>({
    currentQuestion: null,
    questionHistory: [],
    estimatedAbility: INITIAL_ABILITY,
    standardError: 2.0,
    questionsAnswered: 0,
    timeLeft: TIME_LIMIT,
    testStatus: 'instructions',
    currentDifficulty: 5
  });

  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  const [questionPool, setQuestionPool] = useState<AdaptiveQuestion[]>([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const { supabase } = await import('../supabase');
        const tableName = subject === 'Mathematics' ? 'nda' : subject === 'Physics' ? 'jeemain' : 'jeemain';
        const { data: questions } = await supabase
          .from(tableName)
          .select('*')
          .eq('subject', subject)
          .limit(50);
        
        const formattedQuestions = (questions || []).map(q => ({
          id: q.id.toString(),
          text: q.question,
          options: [q.option1, q.option2, q.option3, q.option4].filter(opt => opt),
          answer: q[q.correct] ?? q.correct,
          difficulty: q.level || 5,
          subject: q.subject || subject,
          topic: q.tags || 'General'
        }));
        
        setQuestionPool(formattedQuestions);
        setQuestionsLoaded(true);
      } catch (error) {
        console.error('Error loading questions:', error);
        setQuestionsLoaded(true);
      }
    };
    
    loadQuestions();
  }, [subject]);

  // Item Response Theory (IRT) functions
  const calculateProbability = (ability: number, difficulty: number, discrimination: number = 1) => {
    const exponent = discrimination * (ability - difficulty);
    return 1 / (1 + Math.exp(-exponent));
  };

  const updateAbilityEstimate = useCallback((isCorrect: boolean, questionDifficulty: number) => {
    // Simplified IRT ability estimation using Newton-Raphson method
    const discrimination = 1.0;
    const currentAbility = testState.estimatedAbility;
    
    // Calculate information and score
    const probability = calculateProbability(currentAbility, questionDifficulty, discrimination);
    const information = discrimination * discrimination * probability * (1 - probability);
    const score = isCorrect ? 1 : 0;
    const expectedScore = probability;
    
    // Update ability estimate
    const abilityChange = (score - expectedScore) / information;
    const newAbility = currentAbility + abilityChange;
    
    // Update standard error
    const totalInformation = testState.questionHistory.reduce((sum, item) => {
      const prob = calculateProbability(newAbility, item.question.difficulty);
      return sum + prob * (1 - prob);
    }, information);
    
    const newStandardError = 1 / Math.sqrt(totalInformation);
    
    return { newAbility, newStandardError };
  }, [testState.estimatedAbility, testState.questionHistory]);

  const selectNextQuestion = useCallback((currentAbility: number) => {
    // Select question with difficulty closest to current ability estimate
    const availableQuestions = questionPool.filter(q => 
      !testState.questionHistory.some(h => h.question.id === q.id)
    );
    
    if (availableQuestions.length === 0) return null;
    
    // Find question with difficulty closest to current ability
    const targetDifficulty = Math.max(1, Math.min(10, Math.round(currentAbility + 5))); // Convert to 1-10 scale
    
    let bestQuestion = availableQuestions[0];
    let minDifference = Math.abs(bestQuestion.difficulty - targetDifficulty);
    
    for (const question of availableQuestions) {
      const difference = Math.abs(question.difficulty - targetDifficulty);
      if (difference < minDifference) {
        minDifference = difference;
        bestQuestion = question;
      }
    }
    
    return bestQuestion;
  }, [testState.questionHistory, questionPool]);

  const startTest = () => {
    if (!questionsLoaded || questionPool.length === 0) {
      alert('Questions are still loading. Please wait a moment.');
      return;
    }
    const firstQuestion = selectNextQuestion(INITIAL_ABILITY);
    setTestState(prev => ({
      ...prev,
      testStatus: 'running',
      currentQuestion: firstQuestion,
      currentDifficulty: firstQuestion?.difficulty || 5
    }));
    setQuestionStartTime(Date.now());
  };

  const handleAnswer = (answer: string) => {
    if (!testState.currentQuestion) return;
    
    const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
    const isCorrect = answer === testState.currentQuestion.answer;
    
    // Update ability estimate
    const { newAbility, newStandardError } = updateAbilityEstimate(isCorrect, testState.currentQuestion.difficulty);
    
    // Add to history
    const newHistory = [...testState.questionHistory, {
      question: testState.currentQuestion,
      userAnswer: answer,
      isCorrect,
      timeTaken
    }];
    
    // Check if test should end
    const shouldEnd = newHistory.length >= MAX_QUESTIONS || 
                     newStandardError < 0.3 || // High precision achieved
                     testState.timeLeft <= 0;
    
    if (shouldEnd) {
      const results = {
        estimatedAbility: newAbility,
        standardError: newStandardError,
        questionsAnswered: newHistory.length,
        correctAnswers: newHistory.filter(h => h.isCorrect).length,
        totalTime: TIME_LIMIT - testState.timeLeft + timeTaken,
        history: newHistory,
        subject
      };
      
      setTestState(prev => ({
        ...prev,
        testStatus: 'completed',
        questionHistory: newHistory,
        estimatedAbility: newAbility,
        standardError: newStandardError,
        questionsAnswered: newHistory.length
      }));
      
      onComplete(results);
    } else {
      // Get next question
      const nextQuestion = selectNextQuestion(newAbility);
      
      setTestState(prev => ({
        ...prev,
        currentQuestion: nextQuestion,
        questionHistory: newHistory,
        estimatedAbility: newAbility,
        standardError: newStandardError,
        questionsAnswered: newHistory.length,
        currentDifficulty: nextQuestion?.difficulty || prev.currentDifficulty
      }));
      
      setSelectedAnswer(null);
      setQuestionStartTime(Date.now());
    }
  };

  // Timer effect
  useEffect(() => {
    if (testState.testStatus === 'running' && testState.timeLeft > 0) {
      const timer = setInterval(() => {
        setTestState(prev => ({
          ...prev,
          timeLeft: prev.timeLeft - 1
        }));
      }, 1000);
      return () => clearInterval(timer);
    } else if (testState.testStatus === 'running' && testState.timeLeft <= 0) {
      // Time's up - end test
      const results = {
        estimatedAbility: testState.estimatedAbility,
        standardError: testState.standardError,
        questionsAnswered: testState.questionsAnswered,
        correctAnswers: testState.questionHistory.filter(h => h.isCorrect).length,
        totalTime: TIME_LIMIT,
        history: testState.questionHistory,
        subject,
        timeUp: true
      };
      
      setTestState(prev => ({ ...prev, testStatus: 'completed' }));
      onComplete(results);
    }
  }, [testState.testStatus, testState.timeLeft, testState.estimatedAbility, testState.standardError, testState.questionsAnswered, testState.questionHistory, subject, onComplete]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAbilityLevel = (ability: number) => {
    if (ability < -2) return { level: 'Beginner', color: 'text-red-600', bgColor: 'bg-red-100' };
    if (ability < -1) return { level: 'Below Average', color: 'text-orange-600', bgColor: 'bg-orange-100' };
    if (ability < 1) return { level: 'Average', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    if (ability < 2) return { level: 'Above Average', color: 'text-blue-600', bgColor: 'bg-blue-100' };
    return { level: 'Advanced', color: 'text-green-600', bgColor: 'bg-green-100' };
  };

  const InstructionsScreen = () => (
    <div className="text-center space-y-6 max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-brand-text-primary mb-2">🎯 Adaptive Test</h1>
        <p className="text-xl text-brand-text-secondary">Personalized assessment for {subject}</p>
      </div>

      <div className="bg-brand-surface rounded-xl p-6 text-left space-y-4">
        <h2 className="text-2xl font-semibold text-brand-text-primary mb-4">How Adaptive Testing Works</h2>
        <ul className="space-y-3 text-brand-text-secondary">
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">•</span>
            <span>Questions adapt to your ability level in real-time</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">•</span>
            <span>Correct answers lead to harder questions, incorrect to easier ones</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">•</span>
            <span>Test ends when your ability is accurately estimated (max 20 questions)</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">•</span>
            <span>More efficient than traditional tests - fewer questions, better accuracy</span>
          </li>
        </ul>
      </div>

      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="bg-brand-surface p-4 rounded-lg">
          <div className="text-2xl font-bold text-brand-primary">20</div>
          <div className="text-sm text-brand-text-secondary">Max Questions</div>
        </div>
        <div className="bg-brand-surface p-4 rounded-lg">
          <div className="text-2xl font-bold text-brand-primary">30</div>
          <div className="text-sm text-brand-text-secondary">Minutes</div>
        </div>
        <div className="bg-brand-surface p-4 rounded-lg">
          <div className="text-2xl font-bold text-brand-primary">📊</div>
          <div className="text-sm text-brand-text-secondary">Precise Results</div>
        </div>
      </div>

      <button
        onClick={startTest}
        disabled={!questionsLoaded}
        className="px-8 py-4 bg-brand-primary text-white text-xl font-bold rounded-xl hover:bg-brand-primary-hover transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        {questionsLoaded ? 'Start Adaptive Test' : 'Loading Questions...'}
      </button>
    </div>
  );

  const TestScreen = () => {
    const abilityInfo = getAbilityLevel(testState.estimatedAbility);
    
    return (
      <div className="max-w-4xl mx-auto">
        {/* Test Header */}
        <div className="flex items-center justify-between mb-6 bg-brand-surface rounded-xl p-4">
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="text-sm text-brand-text-secondary">Question</div>
              <div className="text-xl font-bold">{testState.questionsAnswered + 1}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-brand-text-secondary">Difficulty</div>
              <div className="text-xl font-bold">{testState.currentDifficulty}/10</div>
            </div>
            <div className={`px-3 py-1 rounded-lg ${abilityInfo.bgColor}`}>
              <div className="text-xs text-brand-text-secondary">Current Level</div>
              <div className={`font-semibold ${abilityInfo.color}`}>{abilityInfo.level}</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-sm text-brand-text-secondary">Precision</div>
              <div className="text-lg font-bold">{Math.round((1 - testState.standardError / 2) * 100)}%</div>
            </div>
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-mono text-lg ${
              testState.timeLeft <= 300 ? 'bg-red-100 text-red-700' :
              testState.timeLeft <= 600 ? 'bg-yellow-100 text-yellow-700' :
              'bg-green-100 text-green-700'
            }`}>
              <ClockIcon className="w-5 h-5" />
              <span>{formatTime(testState.timeLeft)}</span>
            </div>
          </div>
        </div>

        {/* Question */}
        <div className="bg-brand-surface rounded-xl p-8 mb-6">
          <div className="mb-4">
            <span className="text-sm text-brand-text-secondary">
              {testState.currentQuestion?.subject} • {testState.currentQuestion?.topic}
            </span>
          </div>
          <div className="text-xl text-brand-text-primary mb-6">
            {parseTextWithImages(testState.currentQuestion?.text || '')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {testState.currentQuestion?.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                className={`p-4 text-left border-2 rounded-lg transition-all duration-200 cursor-pointer ${
                  selectedAnswer === option
                    ? 'border-brand-primary bg-brand-primary/10'
                    : 'border-brand-border hover:border-brand-primary/50 hover:bg-brand-primary/5'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="font-bold text-lg">{String.fromCharCode(65 + index)}</span>
                  <span>{parseTextWithImages(option)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="bg-brand-surface rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Test Progress</span>
            <span className="text-sm text-brand-text-secondary">
              {testState.questionsAnswered}/{MAX_QUESTIONS} questions
            </span>
          </div>
          <div className="w-full bg-brand-bg rounded-full h-2">
            <div 
              className="bg-brand-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(testState.questionsAnswered / MAX_QUESTIONS) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-brand-bg rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <TrendingUpIcon className="w-8 h-8 text-brand-primary" />
              <h1 className="text-2xl font-bold text-brand-text-primary">Adaptive Test</h1>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-brand-surface rounded-lg">
              <XIcon className="w-6 h-6" />
            </button>
          </div>

          {testState.testStatus === 'instructions' && <InstructionsScreen />}
          {testState.testStatus === 'running' && <TestScreen />}
        </div>
      </div>
    </div>
  );
};